/**
 * The odds chips and the expected-move band, as pure arithmetic over quoted legs.
 *
 * Both numbers are computed on the web from the per-leg figures the batch endpoint returns,
 * rather than server-side, so a chip can be tested against a fixture with no fetch at all.
 */

/**
 * One leg as `POST /quotes/options/legs/batch` returns it. `expirationDate` is a YYYY-MM-DD day
 * label: it does not match the reviver's `toISOString()` shape, so it stays a string in the
 * browser — which is what `LegRequest` wants anyway.
 */
export interface QuotedLeg {
  optionType: 'call' | 'put';
  strikePrice: number;
  expirationDate: string;
  delta: number | null;
  impliedVolatility: number | null;
}

export interface LegBatchGroup {
  id: string;
  legs: QuotedLeg[];
  /** Non-null when the ladder could not price this group; the rest of the board is unaffected. */
  error: string | null;
}

export interface LegBatchResponse {
  groups: LegBatchGroup[];
}

export interface ShortStrikes {
  shortPut: number | null;
  shortCall: number | null;
}

/** Whole-percent chance each short finishes in the money. */
export interface Odds {
  putProb: number | null;
  callProb: number | null;
}

/** Strikes are decimals off a broker feed; match to the cent rather than by identity. */
const SAME_STRIKE = 0.01;

/**
 * The quoted leg behind each short strike.
 *
 * Exported because the odds and the expected move must read the SAME two legs — deriving the
 * implied volatilities from a second, separately-written matcher is how the chip and the band
 * end up describing different contracts.
 */
export function shortLegQuotes(
  legs: ShortStrikes,
  greeks: QuotedLeg[],
): { put: QuotedLeg | null; call: QuotedLeg | null } {
  const find = (optionType: 'call' | 'put', strike: number | null) =>
    strike === null
      ? null
      : greeks.find(
          (leg) => leg.optionType === optionType && Math.abs(leg.strikePrice - strike) < SAME_STRIKE,
        ) ?? null;
  return { put: find('put', legs.shortPut), call: find('call', legs.shortCall) };
}

const probability = (leg: { delta: number | null } | null): number | null =>
  leg && typeof leg.delta === 'number' && Number.isFinite(leg.delta)
    ? Math.round(Math.abs(leg.delta) * 100)
    : null;

/** Delta read as the probability of finishing in the money — the number Todd picks strikes by. */
export function oddsFromLegs(legs: ShortStrikes, greeks: QuotedLeg[]): Odds {
  const found = shortLegQuotes(legs, greeks);
  return { putProb: probability(found.put), callProb: probability(found.call) };
}

/**
 * One standard deviation of price to expiration: `price x iv x sqrt(dte / 365)`, with `iv` the
 * mean of the implied volatilities that are actually quoted.
 *
 * Null — not zero — when there is nothing to compute from, and null on or past expiration day:
 * a zero-width band would draw as a line at the price and read as a measured "no move expected".
 */
export function expectedMove(
  price: number | null,
  ivs: Array<number | null>,
  dte: number | null,
): number | null {
  if (price === null || !Number.isFinite(price)) return null;
  if (dte === null || !Number.isFinite(dte) || dte <= 0) return null;
  const usable = ivs.filter((iv): iv is number => typeof iv === 'number' && Number.isFinite(iv));
  if (usable.length === 0) return null;
  const iv = usable.reduce((sum, value) => sum + value, 0) / usable.length;
  return price * iv * Math.sqrt(dte / 365);
}
